import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { ApiError } from '@/shared/lib/apiClient';
import { Button, CrayonInput, useToastStore } from '@/shared/ui';

import { useActivateCode } from '../hooks/useActivateCode';
import { useRedemptionState } from '../hooks/useRedemptionState';
import {
  CODE_EXAMPLE,
  CODE_LENGTH,
  codeSchema,
  type CodeFormValues,
} from '../schemas/code.schema';

interface CodeInputFormProps {
  /** 1..3 — 안내 문구에 표시될 현재 차례. */
  step: number;
  /** 검증 성공 후 부모가 호출할 콜백 (옵션). */
  onSuccess?: () => void;
}

/**
 * 코드 1개를 입력받아 서버에 활성화(등록) 요청하는 폼.
 *
 * - React Hook Form + Zod resolver 로 입력 검증.
 * - 활성화 성공 시: 서버가 반환한 `codeCount`/`downloadUrl` 로 redemption state 갱신 + 성공 토스트.
 * - 활성화 실패(ApiError) 시: 에러 토스트 + 입력창 흔들림 (CrayonInput.error prop).
 * - 사용자 코드(`userCode`)가 발급되지 않은 동안에는 입력을 비활성화한다.
 */
export function CodeInputForm({ step, onSuccess }: CodeInputFormProps): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const pushToast = useToastStore((s) => s.push);
  const { state, applyStatus } = useRedemptionState();

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CodeFormValues>({
    resolver: zodResolver(codeSchema),
    mode: 'onSubmit',
  });

  const activate = useActivateCode();

  // 차례가 바뀔 때 입력창 자동 포커스
  useEffect(() => {
    inputRef.current?.focus();
  }, [step]);

  const onSubmit = handleSubmit(async ({ code }) => {
    if (state.userCode === null) {
      // 가드 — 자동 발급이 끝나기 전 폼이 활성화되는 일은 없으나 방어적으로 처리.
      pushToast({ variant: 'info', message: '유저 코드 발급이 끝난 뒤 다시 시도해 주세요.' });
      return;
    }
    try {
      const result = await activate.mutateAsync({ userCode: state.userCode, code });
      applyStatus({
        codeCount: result.codeCount,
        downloadUrl: result.downloadUrl,
      });
      pushToast({
        variant: 'success',
        message: `코드를 등록했어요! (${String(result.codeCount)}/3)`,
      });
      reset();
      onSuccess?.();
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : '알 수 없는 오류가 발생했어요. 잠시 후 다시 시도해 주세요.';
      pushToast({ variant: 'error', message });
      setError('code', { type: 'server', message });
    }
  });

  // register와 ref 병합
  const { ref: hookRef, ...rest } = register('code');

  const showError = Boolean(errors.code);
  const errorMessage = errors.code?.message;

  // 유저 코드가 아직 발급되지 않은 동안에는 코드 입력을 막아 둔다.
  // 자동 발급은 보통 1초 이내에 완료되므로 사용자가 거의 체감하지 않는다.
  const awaitingUserCode = state.userCode === null;
  const inputDisabled = isSubmitting || awaitingUserCode;

  const helperBase = `방송 화면에 노출된 코드를 입력하세요. (예: ${CODE_EXAMPLE})`;
  const helper = awaitingUserCode
    ? '유저 코드 발급이 끝나면 입력할 수 있어요.'
    : helperBase;

  return (
    <form onSubmit={onSubmit} noValidate>
      <CrayonInput
        {...rest}
        ref={(el) => {
          hookRef(el);
          inputRef.current = el;
        }}
        label={`코드를 입력해 주세요 (${String(step)}/3)`}
        placeholder={CODE_EXAMPLE}
        helperText={helper}
        maxLength={CODE_LENGTH}
        error={showError}
        errorText={errorMessage}
        disabled={inputDisabled}
      />
      <div style={{ marginTop: 'var(--space-4)' }}>
        <Button type="submit" variant="primary" fullWidth disabled={inputDisabled}>
          {isSubmitting
            ? '확인 중…'
            : awaitingUserCode
              ? '유저 코드 준비 중…'
              : '코드 등록'}
        </Button>
      </div>
    </form>
  );
}
